import React, { useContext, useEffect, useState } from 'react';
import AOS from 'aos';
import { useLocation } from 'react-router-dom';
import { store } from '../App';
import { Col, Container, Row } from 'react-bootstrap';
import Footer from './Footer';
import axios from 'axios';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet';
import { apiUrl } from './Constants';
import { FiTarget, FiCompass, FiShield, FiUsers, FiHeart, FiAward } from 'react-icons/fi';

const cleanHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    return html
        .replace(/<pre[^>]*id=["']tw-target-text["'][^>]*>/gi, '<div>')
        .replace(/<\/pre>/gi, '</div>')
        .replace(/style=["'][^"']*(?:width:\s*270px|background-color:\s*rgb\(248,\s*249,\s*250\))[^"']*["']/gi, '')
        .replace(/<div class=["']about-page-card[^"']*["'][\s\S]*?<\/div>\s*<\/div>\s*<\/div>/gi, '')
        .trim();
};

const safeParse = (htmlString, fallback = null) => {
    if (typeof htmlString === 'string' && htmlString.trim().length > 0) {
        try {
            const cleaned = cleanHtml(htmlString);
            return parse(cleaned);
        } catch (e) {
            console.error("HTML parse error:", e);
            return fallback;
        }
    }
    return fallback;
};

const getGeneralSectionImage = (section, fallback) => {
    const file = section?.icon_file || section?.image || section?.photo;
    if (!file) return fallback;
    const trimmed = String(file).trim();
    if (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('/') ||
        trimmed.startsWith('data:')
    ) {
        return trimmed;
    }
    return `https://dev10.sbagh.com/public/images/general-section/large/${trimmed}`;
};

function About() {
    const [lang] = useContext(store);
    const location = useLocation();

    // Determine current slug from the route path (e.g. 'about-foton', 'about-ggs', 'about-us')
    const currentSlug = location.pathname.toLowerCase().replace(/^\/+|\/+$/g, '') || 'about-us';
    const isFoton = currentSlug.includes('foton');
    const isGgs = currentSlug.includes('ggs');
    const isAboutUs = !isFoton && !isGgs;

    const [aboutData, setAboutData] = useState(null);
    const [seo, setSeo] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const baseEndpoint = apiUrl || 'https://dev10.sbagh.com/api/';

        const fetchAboutData = async () => {
            try {
                // Request the specific slug from the API
                const res = await axios.get(`${baseEndpoint}getAboutUsPageDataBySlug/${currentSlug}`);
                const data = res?.data?.data;
                const specificSections = data?.[currentSlug];

                const hasSpecificData =
                    specificSections &&
                    (Array.isArray(specificSections)
                        ? specificSections.length > 0
                        : Object.keys(specificSections).length > 0);

                if (hasSpecificData) {
                    if (isMounted) {
                        setAboutData(specificSections);
                        setSeo(data?.seo || null);
                    }
                    return;
                }

                // If specific slug returned empty array (e.g. dev10 child slugs), fall back to 'about-us'
                const fallbackRes = await axios.get(`${baseEndpoint}getAboutUsPageDataBySlug/about-us`);
                if (fallbackRes?.data?.data && isMounted) {
                    const fallbackData = fallbackRes.data.data;
                    setAboutData(fallbackData['about-us'] || fallbackData);
                    setSeo(data?.seo && Object.keys(data.seo).length > 0 ? data.seo : fallbackData.seo);
                    return;
                }
            } catch (err) {
                console.warn(`Could not load /${currentSlug} data, attempting about-us fallback:`, err?.message);
                try {
                    const fallbackRes = await axios.get(`${baseEndpoint}getAboutUsPageDataBySlug/about-us`);
                    if (fallbackRes?.data?.data && isMounted) {
                        const fallbackData = fallbackRes.data.data;
                        setAboutData(fallbackData['about-us'] || fallbackData);
                        setSeo(fallbackData.seo);
                        return;
                    }
                } catch (fallbackErr) {
                    console.error("About page fallback fetch failed:", fallbackErr);
                }
            }
        };

        fetchAboutData();
        return () => {
            isMounted = false;
        };
    }, [currentSlug]);

    useEffect(() => {
        if (aboutData) {
            const timer = setTimeout(() => {
                AOS.refresh();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [aboutData]);

    // Extract individual sections from API data
    const headerImage = aboutData?.header_image;
    const ggsLlc = aboutData?.about_ggs_llc;
    const mission = aboutData?.mission_summery;
    const fotonOverview = aboutData?.foton_overview;
    const globalFoton = aboutData?.global_foton;

    // Resolve images
    const headerBannerSrc = getGeneralSectionImage(headerImage, '/Images/logo/Medium-Heavy-Duty-Truck-2.jpg');
    const fotonOverviewImgSrc = getGeneralSectionImage(fotonOverview, '/Images/Card-img/Media/AutoCare.webp');
    const globalFotonImgSrc = getGeneralSectionImage(globalFoton, '/Images/Card-img/Media/globalization-1024x484.webp');

    // Page Title
    const defaultTitle = isFoton
        ? (lang ? "حول فوتون | فوتون عمان" : "About Foton | Foton Oman")
        : isGgs
        ? (lang ? "حول رمال الخليج الكبرى | فوتون عمان" : "About GGS | Foton Oman")
        : (lang ? "معلومات عنا | فوتون عمان" : "About Us | Foton Oman");

    return (
        <div className="about-page-wrapper" dir={lang ? "rtl" : "ltr"}>
            <Helmet>
                <title>{seo?.meta_title || defaultTitle}</title>
                {seo?.meta_description && <meta name="description" content={seo.meta_description} />}
                {seo?.meta_keywords && <meta name="keywords" content={seo.meta_keywords} />}
                {seo?.canonical && <link rel="canonical" href={seo.canonical} />}
            </Helmet>

            <Container fluid className="px-0">
                {/* Hero Header Banner */}
                <Row className="mx-0">
                    <Col lg={12} xs={12} style={{ padding: '0px' }}>
                        <img
                            src={headerBannerSrc}
                            alt={headerImage?.category_title_en || "Foton Header Banner"}
                            width="100%"
                            style={{ maxHeight: '420px', objectFit: 'cover', display: 'block' }}
                            onError={(e) => {
                                e.target.src = '/Images/logo/Medium-Heavy-Duty-Truck-2.jpg';
                            }}
                        />
                    </Col>
                </Row>

                {/* Section 1: Gulf Great Sands LLC (Redesigned matching baicoman.com/about/ggs) */}
                {(isGgs || isAboutUs) && (
                    <section className="about-ggs-section">
                        <div className="ggs-page-container">
                            {/* Intro Block */}
                            <div className="ggs-intro-block" data-aos="fade-up">
                                <h2 className="ggs-intro-title">
                                    {lang
                                        ? (ggsLlc?.category_title_ar || "رمال الخليج الكبرى")
                                        : (ggsLlc?.category_title_en || "Gulf Great Sands LLC")}
                                </h2>
                                <div className="ggs-intro-text">
                                    {ggsLlc && (ggsLlc.description_en || ggsLlc.description_ar) ? (
                                        lang
                                            ? safeParse(
                                                  ggsLlc.description_ar || ggsLlc.description_en,
                                                  <>
                                                      <p>تأسست شركة رمال الخليج الكبرى في عام 2016 في سلطنة عمان، وبنت سمعة قوية في دعم نمو وتطوير الصناعات في جميع أنحاء البلاد. تأسست الشركة بشغف بالتميز ورؤية للمستقبل، وتواصل التطور من خلال الابتكار والموثوقية والحلول التي تركز على العملاء.</p>
                                                      <p>تتخصص شركة رمال الخليج الكبرى في حلول المركبات التجارية وسيارات الركاب، حيث تقدم حلول تنقل موثوقة لمختلف احتياجات الأعمال. وبصفتنا الموزع الرسمي لـ فوتون في سلطنة عمان، فإننا نقدم مجموعة شاملة من المركبات المدعومة بالخبرة العالمية والتكنولوجيا المتقدمة ودعم ما بعد البيع الموثوق به.</p>
                                                  </>
                                              )
                                            : safeParse(
                                                  ggsLlc.description_en || ggsLlc.description_ar,
                                                  <>
                                                      <p>Gulf Great Sands LLC was established in 2016 in Oman and has built a strong reputation for supporting the growth and development of industries across the country. Founded with a passion for excellence and a forward-looking vision, the company continues to evolve through innovation, reliability, and customer-focused solutions.</p>
                                                      <p>Gulf Great Sands LLC specializes in automotive commercial and passenger vehicle solutions, delivering dependable mobility for diverse business needs. As the official distributor of Foton in Oman, we provide a comprehensive range of vehicles backed by global expertise, advanced technology, and trusted aftersales support—ensuring lasting value for our customers.</p>
                                                  </>
                                              )
                                    ) : (
                                        <>
                                            <p>{lang ? "تأسست شركة رمال الخليج الكبرى في عام 2016 في سلطنة عمان، وبنت سمعة قوية في دعم نمو وتطوير الصناعات في جميع أنحاء البلاد. تأسست الشركة بشغف بالتميز ورؤية للمستقبل، وتواصل التطور من خلال الابتكار والموثوقية والحلول التي تركز على العملاء." : "Gulf Great Sands LLC was established in 2016 in Oman and has built a strong reputation for supporting the growth and development of industries across the country. Founded with a passion for excellence and a forward-looking vision, the company continues to evolve through innovation, reliability, and customer-focused solutions."}</p>
                                            <p>{lang ? "تتخصص شركة رمال الخليج الكبرى في حلول المركبات التجارية وسيارات الركاب، حيث تقدم حلول تنقل موثوقة لمختلف احتياجات الأعمال. وبصفتنا الموزع الرسمي لـ فوتون في سلطنة عمان، فإننا نقدم مجموعة شاملة من المركبات المدعومة بالخبرة العالمية والتكنولوجيا المتقدمة ودعم ما بعد البيع الموثوق به." : "Gulf Great Sands LLC specializes in automotive commercial and passenger vehicle solutions, delivering dependable mobility for diverse business needs. As the official distributor of Foton in Oman, we provide a comprehensive range of vehicles backed by global expertise, advanced technology, and trusted aftersales support—ensuring lasting value for our customers."}</p>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Vision & Mission Cards Grid (Matching BAIC Oman /about/ggs design) */}
                            <div className="ggs-vm-grid">
                                {/* Vision Card */}
                                <div className="ggs-vm-card ggs-vm-card-vision" data-aos="fade-up" data-aos-delay="100">
                                    <div className="ggs-vm-card-corner" />
                                    <div className="ggs-vm-card-content">
                                        <div className="ggs-vm-icon-box">
                                            <FiTarget />
                                        </div>
                                        <h3 className="ggs-vm-title">
                                            {lang ? "الرؤية" : "Vision"}
                                        </h3>
                                        <p className="ggs-vm-desc">
                                            {lang
                                                ? '"مجموعة أعمال معترف بها عالمياً تصنع عملاء مدى الحياة."'
                                                : '"A globally recognized business group creating customers for life."'}
                                        </p>
                                    </div>
                                </div>

                                {/* Mission Card */}
                                <div className="ggs-vm-card ggs-vm-card-mission" data-aos="fade-up" data-aos-delay="200">
                                    <div className="ggs-vm-card-corner" />
                                    <div className="ggs-vm-card-content">
                                        <div className="ggs-vm-icon-box">
                                            <FiCompass />
                                        </div>
                                        <h3 className="ggs-vm-title">
                                            {lang
                                                ? (mission?.category_title_ar || "المهمة")
                                                : (mission?.category_title_en || "Mission")}
                                        </h3>
                                        <p className="ggs-vm-desc">
                                            {mission ? (
                                                lang
                                                    ? safeParse(
                                                          mission.description_ar || mission.description_en,
                                                          '"تنويع وتقديم منتجات وخدمات ذات مستوى عالمي بشكل مبتكر، من خلال تمكين الموظفين وخلق قيمة لأصحاب المصلحة لدينا."'
                                                      )
                                                    : safeParse(
                                                          mission.description_en || mission.description_ar,
                                                          '"Diversify and innovatively deliver world-class products and services, through empowered employees by creating value for our stakeholders."'
                                                      )
                                            ) : (
                                                lang
                                                    ? '"تنويع وتقديم منتجات وخدمات ذات مستوى عالمي بشكل مبتكر، من خلال تمكين الموظفين وخلق قيمة لأصحاب المصلحة لدينا."'
                                                    : '"Diversify and innovatively deliver world-class products and services, through empowered employees by creating value for our stakeholders."'
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Core Values 4-Card Grid (Matching BAIC Oman /about/ggs design) */}
                            <div className="ggs-values-grid">
                                <div className="ggs-value-card" data-aos="fade-up" data-aos-delay="100">
                                    <div className="ggs-value-icon-circle">
                                        <FiShield />
                                    </div>
                                    <h4 className="ggs-value-title">
                                        {lang ? "نزاهة" : "Integrity"}
                                    </h4>
                                </div>
                                <div className="ggs-value-card" data-aos="fade-up" data-aos-delay="200">
                                    <div className="ggs-value-icon-circle">
                                        <FiUsers />
                                    </div>
                                    <h4 className="ggs-value-title">
                                        {lang ? "ملكية" : "Ownership"}
                                    </h4>
                                </div>
                                <div className="ggs-value-card" data-aos="fade-up" data-aos-delay="300">
                                    <div className="ggs-value-icon-circle">
                                        <FiHeart />
                                    </div>
                                    <h4 className="ggs-value-title">
                                        {lang ? "شغف" : "Passion"}
                                    </h4>
                                </div>
                                <div className="ggs-value-card" data-aos="fade-up" data-aos-delay="400">
                                    <div className="ggs-value-icon-circle">
                                        <FiAward />
                                    </div>
                                    <h4 className="ggs-value-title">
                                        {lang ? "تفوق" : "Excellence"}
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Section 2: Foton Overview & Global Foton (Shown on /about-foton and /about-us) */}
                {(isFoton || isAboutUs) && (
                    <section className="about-foton-section my-5">
                        {/* Foton Overview Heading */}
                        <h3 className="text-center foton-section-heading" data-aos="fade-up">
                            {lang
                                ? (fotonOverview?.category_title_ar || "نظرة عامة على الفوتون")
                                : (fotonOverview?.category_title_en || "Foton Overview")}
                        </h3>
                        <div className="text-center text-muted mb-4 foton-overview-highlight" data-aos="fade-up">
                            {fotonOverview ? (
                                lang
                                    ? safeParse(
                                          fotonOverview.highlight_ar || fotonOverview.highlight_en,
                                          "قيادة التطور المتسارع في صناعة المركبات التجارية"
                                      )
                                    : safeParse(
                                          fotonOverview.highlight_en || fotonOverview.highlight_ar,
                                          "Driving accelerated development in Commercial Vehicle industry"
                                      )
                            ) : (
                                <p>Driving accelerated development in Commercial Vehicle industry</p>
                            )}
                        </div>

                        {/* Foton Overview Grid Card */}
                        <Row className="Foton-Overview-2 justify-content-center mx-0">
                            <Col lg={11}>
                                <Row className="mb-5 mt-4 align-items-center" style={{ minHeight: '360px' }}>
                                    <Col lg={6} md={6} xs={11} className="mb-3 mb-md-0" data-aos="fade-up">
                                        <img
                                            src={fotonOverviewImgSrc}
                                            height="100%"
                                            width="100%"
                                            alt={fotonOverview?.category_title_en || "Foton Overview"}
                                            style={{ borderRadius: '6px', objectFit: 'cover' }}
                                            onError={(e) => {
                                                e.target.src = '/Images/Card-img/Media/AutoCare.webp';
                                            }}
                                        />
                                    </Col>
                                    <Col lg={6} md={6} xs={12} className="about-foton-page-down" data-aos="fade-up" data-aos-delay="150">
                                        <div className="foton-overview-text px-lg-3">
                                            {fotonOverview ? (
                                                lang
                                                    ? safeParse(
                                                          fotonOverview.description_ar || fotonOverview.description_en,
                                                          <div>
                                                              <h3>من أجل النقل الخالي من الوقود الأحفوري</h3>
                                                              <p>تأسست شركة فوتون موتورز في 28 أغسطس 1996، ويقع المقر الرئيسي لشركة فوتون موتورز في بكين، الصين...</p>
                                                          </div>
                                                      )
                                                    : safeParse(
                                                          fotonOverview.description_en || fotonOverview.description_ar,
                                                          <div>
                                                              <h3>For Fossil Free Transportation</h3>
                                                              <p>Founded on 28th August, 1996, Foton Motor is headquartered in Beijing, China. With a business scope covering a full series of Commercial Vehicles including Light, Medium and Heavy Duty Trucks, as well as Mini Truck, Vans, Pickup, and Construction Vehicles. Foton Motor has become one of the leading Commercial Vehicle manufactures in the world. Foton Motor has leading new energy technology including electric and hydrogen vehicles.</p>
                                                          </div>
                                                      )
                                            ) : (
                                                <div>
                                                    <h3>For Fossil Free Transportation</h3>
                                                    <p>Founded on 28th August, 1996, Foton Motor is headquartered in Beijing, China. With a business scope covering a full series of Commercial Vehicles including Light, Medium and Heavy Duty Trucks, as well as Mini Truck, Vans, Pickup, and Construction Vehicles. Foton Motor has become one of the leading Commercial Vehicle manufactures in the world. Foton Motor has leading new energy technology including electric and hydrogen vehicles.</p>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>

                                {/* Global Foton Heading */}
                                <h3 className="text-center mt-5 foton-section-heading" data-aos="fade-up">
                                    {lang
                                        ? (globalFoton?.category_title_ar || "فوتون العالمية")
                                        : (globalFoton?.category_title_en || "Global Foton")}
                                </h3>
                                <div className="text-center text-muted mb-4 global-foton-highlight" data-aos="fade-up">
                                    {globalFoton ? (
                                        lang
                                            ? safeParse(
                                                  globalFoton.highlight_ar || globalFoton.highlight_en,
                                                  "نسعى جاهدين لنكون الشركة المصنعة للمركبات التجارية الرائدة عالميًا"
                                              )
                                            : safeParse(
                                                  globalFoton.highlight_en || globalFoton.highlight_ar,
                                                  "Striving to be global leading Commercial Vehicle manufacturer"
                                              )
                                    ) : (
                                        <p>Striving to be global leading Commercial Vehicle manufacturer</p>
                                    )}
                                </div>

                                {/* Global Map Image */}
                                <div className="text-center mb-5" data-aos="fade-up">
                                    <img
                                        className="img-fluid"
                                        src={globalFotonImgSrc}
                                        alt={globalFoton?.category_title_en || "Global Foton Footprint"}
                                        style={{ borderRadius: '6px', width: '100%' }}
                                        onError={(e) => {
                                            e.target.src = '/Images/Card-img/Media/globalization-1024x484.webp';
                                        }}
                                    />
                                </div>

                                {/* Global Foton Stats Grid */}
                                <Row className="mb-5 about-page-row text-center g-3">
                                    <Col lg={3} sm={6} xs={6} data-aos="fade-up" data-aos-delay="100">
                                        <div className="about-stat-box">
                                            <h4 className="stat-number">10,000,000+</h4>
                                            <p className="stat-label">
                                                {lang ? "المبيعات التراكمية" : "Accumulative Sales"}
                                            </p>
                                        </div>
                                    </Col>
                                    <Col lg={3} sm={6} xs={6} data-aos="fade-up" data-aos-delay="200">
                                        <div className="about-stat-box">
                                            <h4 className="stat-number">2,000+</h4>
                                            <p className="stat-label">
                                                {lang ? "شبكة التوزيع الدولية" : "Overseas Network"}
                                            </p>
                                        </div>
                                    </Col>
                                    <Col lg={3} sm={6} xs={6} data-aos="fade-up" data-aos-delay="300">
                                        <div className="about-stat-box">
                                            <h4 className="stat-number">22+</h4>
                                            <p className="stat-label">
                                                {lang ? "مصانع تجميع KD" : "KD Factories"}
                                            </p>
                                        </div>
                                    </Col>
                                    <Col lg={3} sm={6} xs={6} data-aos="fade-up" data-aos-delay="400">
                                        <div className="about-stat-box">
                                            <h4 className="stat-number">110+</h4>
                                            <p className="stat-label">
                                                {lang ? "دولة مغطاة" : "Countries Covered"}
                                            </p>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </section>
                )}
            </Container>

            <Footer />
        </div>
    );
}

export default About;

